/**
 * Decision latency — the pure read model (Decision Latency Instrumentation PRD, L1).
 *
 * "Analysis takes 30 minutes; decisions still take weeks." The pipeline is
 * already timestamped end to end — nine tables carry the stamps — but nothing
 * ever decomposed the elapsed time into named stages. This module does that
 * arithmetic and nothing else.
 *
 * NO new timestamps, NO writes, NO Supabase imports. Every number here is a
 * subtraction between two stamps that already exist. The fetch layers are
 * `src/lib/decisionLatencyService.ts` (the app) and
 * `supabase/functions/api/routes/decision-latency.ts` (the public API); both
 * hand the rows they read to `assembleLatencyChains` below, so the pairing of
 * loops, proposals, sessions and reports is one rule, not two.
 *
 * THE ONE INVARIANT — null-honesty:
 *
 *   A missing stamp yields an `unmeasured` segment carrying the name of the
 *   stamp that was missing. It is never interpolated, never defaulted to zero,
 *   and never quietly dropped from an aggregate. Same discipline as "overdue is
 *   computed, never stored": an unmeasured segment is a fact about our
 *   instrumentation, and a report that hides it is worse than no report — an
 *   executive challenging "prove we're slow at approval, not deliberation"
 *   deserves to see which segments we could not measure at all.
 *
 * Out-of-order stamps get the same treatment. Historical rows really do carry
 * end-before-start pairs (nullable `proposed_at` was backfilled unevenly), and
 * a negative duration silently averaged into a p90 is a fabricated number. They
 * are surfaced as `anomalous`, excluded from aggregates, and counted.
 */

// ---------------------------------------------------------------------------
// §1 — Vocabulary
// ---------------------------------------------------------------------------

/**
 * Stage names are Sahar's decomposition, deliberately in buyer language rather
 * than catchball's internal state vocabulary (PRD open question 1). They are
 * fixed in this one place; every surface reads them from here.
 */
export const LATENCY_STAGES = [
  'framing',
  'deliberation',
  'proposal',
  'approval_queue',
  'resolution',
  'ratification',
  'authorization',
] as const;

export type LatencyStage = (typeof LATENCY_STAGES)[number];

/** Human-facing labels and what each segment actually spans. */
export const STAGE_LABELS: Record<LatencyStage, string> = {
  framing: 'Framing',
  deliberation: 'Deliberation',
  proposal: 'Proposal',
  approval_queue: 'Approval queue',
  resolution: 'Resolution',
  ratification: 'Ratification',
  authorization: 'Authorization',
};

export const STAGE_SPANS: Record<LatencyStage, string> = {
  framing: 'Ask issued → deliberation starts',
  deliberation: 'Session opened → run complete',
  proposal: 'Packaged → proposed',
  approval_queue: 'Proposed → responded',
  resolution: 'Responded → resolved',
  ratification: 'Report created → ratified',
  authorization: 'Ratified → Plan of Record authorized',
};

export type SegmentState =
  /** Both stamps present and ordered. `durationMs` is a real number. */
  | 'measured'
  /** At least one stamp is missing. `unmeasuredReason` names which. */
  | 'unmeasured'
  /** Both stamps present but end precedes start. Excluded from aggregates. */
  | 'anomalous';

export interface StageSegment {
  stage: LatencyStage;
  state: SegmentState;
  startedAt: string | null;
  endedAt: string | null;
  /** Non-null only when `state === 'measured'`. */
  durationMs: number | null;
  /** Non-null when the segment is `unmeasured` or `anomalous`. */
  reason: string | null;
}

/**
 * The stamps a single decision chain can carry. Every field is nullable
 * because every field really is nullable in the data — see the PRD's
 * "known weak links". Field names mirror their source columns so a reader can
 * trace any number back to a table.
 */
export interface LatencyChainInput {
  /** Identifier for the chain — a catchball loop id, or a cycle id. */
  id: string;
  label?: string | null;

  /** catchball_loops */
  requestIssuedAt?: string | null;
  ballClaimedAt?: string | null;
  proposalSubmittedAt?: string | null;

  /** prioritization_sessions */
  sessionCreatedAt?: string | null;
  criteriaFinalizedAt?: string | null;
  runCompletedAt?: string | null;

  /** catchball_proposals */
  packagedAt?: string | null;
  proposedAt?: string | null;
  respondedAt?: string | null;
  proposalResolvedAt?: string | null;

  /** cycle_reports */
  reportCreatedAt?: string | null;
  reportRatifiedAt?: string | null;

  /**
   * plan_of_record_snapshots / por_record_snapshots.
   *
   * Reaching this from a cycle report requires `source_cycle_report_id`, which
   * migration 678 deliberately left NULL ("wiring it is a follow-up, not
   * silently guessed here"). Until the propagation PRD's P1 lands there is no
   * join, so callers cannot populate this and the authorization segment reports
   * `unmeasured` by construction rather than appearing broken.
   */
  porAuthorizedAt?: string | null;
}

export interface LatencyChain {
  id: string;
  label: string | null;
  segments: StageSegment[];
  /** Sum of measured segments only. Never a total that spans a gap. */
  measuredTotalMs: number;
  measuredCount: number;
  unmeasuredCount: number;
  anomalousCount: number;
  /** True when every stage is measured — the chains that carry a real total. */
  complete: boolean;
  /** Longest measured segment, the headline of the per-chain drill-in. */
  dominantStage: LatencyStage | null;
}

// ---------------------------------------------------------------------------
// §2 — Segment construction
// ---------------------------------------------------------------------------

function parseStamp(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Builds one segment from a start/end stamp pair.
 *
 * `startName` / `endName` are the source column names; they land verbatim in
 * the `reason` so a reader of the report knows exactly which stamp to go fix.
 * That is the mechanism by which this report drives its own stamp hygiene —
 * PRD success metric 1.
 */
export function buildSegment(
  stage: LatencyStage,
  start: string | null | undefined,
  end: string | null | undefined,
  startName: string,
  endName: string,
  overrideReason?: string,
): StageSegment {
  const startMs = parseStamp(start);
  const endMs = parseStamp(end);
  const startedAt = startMs === null ? null : (start as string);
  const endedAt = endMs === null ? null : (end as string);

  if (startMs === null || endMs === null) {
    const missing: string[] = [];
    if (startMs === null) missing.push(startName);
    if (endMs === null) missing.push(endName);
    return {
      stage,
      state: 'unmeasured',
      startedAt,
      endedAt,
      durationMs: null,
      reason: overrideReason ?? `missing ${missing.join(' and ')}`,
    };
  }

  if (endMs < startMs) {
    return {
      stage,
      state: 'anomalous',
      startedAt,
      endedAt,
      durationMs: null,
      reason: `${endName} precedes ${startName}`,
    };
  }

  return {
    stage,
    state: 'measured',
    startedAt,
    endedAt,
    durationMs: endMs - startMs,
    reason: null,
  };
}

/**
 * The framing segment ends wherever deliberation actually began. Sessions are
 * the usual start, but a loop whose ball was claimed without a session still
 * has a real framing duration, so `ball_claimed_at` is the documented fallback
 * rather than a silent NULL. Which stamp was used lands in the reason when it
 * is the fallback, so the number is never ambiguous.
 */
function buildFramingSegment(input: LatencyChainInput): StageSegment {
  if (input.sessionCreatedAt) {
    return buildSegment(
      'framing',
      input.requestIssuedAt,
      input.sessionCreatedAt,
      'catchball_loops.request_issued_at',
      'prioritization_sessions.created_at',
    );
  }
  if (input.ballClaimedAt) {
    const seg = buildSegment(
      'framing',
      input.requestIssuedAt,
      input.ballClaimedAt,
      'catchball_loops.request_issued_at',
      'catchball_loops.ball_claimed_at',
    );
    return seg.state === 'measured'
      ? { ...seg, reason: 'measured to ball_claimed_at — no session on this loop' }
      : seg;
  }
  return buildSegment(
    'framing',
    input.requestIssuedAt,
    null,
    'catchball_loops.request_issued_at',
    'prioritization_sessions.created_at',
  );
}

export const AUTHORIZATION_BLOCKED_REASON =
  'cycle report → POR not joinable until source_cycle_report_id is wired (mig 678 note; propagation PRD P1)';

/** Builds the full seven-stage chain for one loop/cycle. */
export function buildLatencyChain(input: LatencyChainInput): LatencyChain {
  const segments: StageSegment[] = [
    buildFramingSegment(input),
    buildSegment(
      'deliberation',
      input.sessionCreatedAt,
      input.runCompletedAt,
      'prioritization_sessions.created_at',
      'prioritization_sessions.run_completed_at',
    ),
    buildSegment(
      'proposal',
      input.packagedAt,
      input.proposedAt,
      'catchball_proposals.packaged_at',
      'catchball_proposals.proposed_at',
    ),
    buildSegment(
      'approval_queue',
      input.proposedAt,
      input.respondedAt,
      'catchball_proposals.proposed_at',
      'catchball_proposals.responded_at',
    ),
    buildSegment(
      'resolution',
      input.respondedAt,
      input.proposalResolvedAt,
      'catchball_proposals.responded_at',
      'catchball_proposals.resolved_at',
    ),
    buildSegment(
      'ratification',
      input.reportCreatedAt,
      input.reportRatifiedAt,
      'cycle_reports.created_at',
      'cycle_reports.ratified_at',
    ),
    buildSegment(
      'authorization',
      input.reportRatifiedAt,
      input.porAuthorizedAt,
      'cycle_reports.ratified_at',
      'plan_of_record_snapshots.authorized_at',
      input.porAuthorizedAt ? undefined : AUTHORIZATION_BLOCKED_REASON,
    ),
  ];

  const measured = segments.filter((s) => s.state === 'measured');
  const measuredTotalMs = measured.reduce((sum, s) => sum + (s.durationMs ?? 0), 0);

  let dominantStage: LatencyStage | null = null;
  let dominantMs = -1;
  for (const seg of measured) {
    if ((seg.durationMs ?? 0) > dominantMs) {
      dominantMs = seg.durationMs ?? 0;
      dominantStage = seg.stage;
    }
  }

  return {
    id: input.id,
    label: input.label ?? null,
    segments,
    measuredTotalMs,
    measuredCount: measured.length,
    unmeasuredCount: segments.filter((s) => s.state === 'unmeasured').length,
    anomalousCount: segments.filter((s) => s.state === 'anomalous').length,
    complete: measured.length === LATENCY_STAGES.length,
    dominantStage,
  };
}

// ---------------------------------------------------------------------------
// §3 — Aggregates
// ---------------------------------------------------------------------------

export interface StageAggregate {
  stage: LatencyStage;
  /** Chains where this stage was measurable. Aggregates are over these only. */
  measuredChains: number;
  unmeasuredChains: number;
  anomalousChains: number;
  medianMs: number | null;
  p90Ms: number | null;
  maxMs: number | null;
  /**
   * Share of measured time across all chains that this stage accounts for.
   * Comparable only between stages with similar `measuredChains` — the report
   * must not rank a stage measured twice against one measured forty times.
   */
  shareOfMeasuredTime: number | null;
}

export interface LatencyAggregate {
  chains: number;
  completeChains: number;
  stages: StageAggregate[];
  /** Measured share of all segments — the stamp-hygiene metric (PRD metric 1). */
  measurableRatio: number;
  /** Highest p90 among stages with at least `minChains` measured. */
  p90OffenderStage: LatencyStage | null;
}

/** Exact-median percentile with linear interpolation. Input need not be sorted. */
// reuse-ack: mirrors src/lib/planningMonteCarlo.ts percentile — an existing pair (this file moved from src/lib to _shared unchanged so the API can import it); _shared code cannot import from src, and this one takes unsorted input and answers null when empty.
export function percentile(values: number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const rank = (sorted.length - 1) * p;
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
}

/**
 * Rolls chains up per stage.
 *
 * `minChainsForOffender` guards the headline: naming a p90 offender off a
 * single measured chain would be a fabricated diagnosis, and this report's
 * whole purpose is surviving an executive's challenge.
 */
export function aggregateLatency(
  chains: LatencyChain[],
  options: { minChainsForOffender?: number } = {},
): LatencyAggregate {
  const minChains = options.minChainsForOffender ?? 3;

  const totalMeasuredMs = chains.reduce((sum, c) => sum + c.measuredTotalMs, 0);

  const stages: StageAggregate[] = LATENCY_STAGES.map((stage) => {
    const segs = chains
      .map((c) => c.segments.find((s) => s.stage === stage))
      .filter((s): s is StageSegment => Boolean(s));

    const durations = segs
      .filter((s) => s.state === 'measured')
      .map((s) => s.durationMs ?? 0);

    const stageTotal = durations.reduce((sum, d) => sum + d, 0);

    return {
      stage,
      measuredChains: durations.length,
      unmeasuredChains: segs.filter((s) => s.state === 'unmeasured').length,
      anomalousChains: segs.filter((s) => s.state === 'anomalous').length,
      medianMs: percentile(durations, 0.5),
      p90Ms: percentile(durations, 0.9),
      maxMs: durations.length ? Math.max(...durations) : null,
      shareOfMeasuredTime: totalMeasuredMs > 0 ? stageTotal / totalMeasuredMs : null,
    };
  });

  const totalSegments = chains.length * LATENCY_STAGES.length;
  const measuredSegments = chains.reduce((sum, c) => sum + c.measuredCount, 0);

  let p90OffenderStage: LatencyStage | null = null;
  let worstP90 = -1;
  for (const s of stages) {
    if (s.measuredChains >= minChains && (s.p90Ms ?? -1) > worstP90) {
      worstP90 = s.p90Ms ?? -1;
      p90OffenderStage = s.stage;
    }
  }

  return {
    chains: chains.length,
    completeChains: chains.filter((c) => c.complete).length,
    stages,
    measurableRatio: totalSegments > 0 ? measuredSegments / totalSegments : 0,
    p90OffenderStage,
  };
}

// ---------------------------------------------------------------------------
// §4 — Formatting
// ---------------------------------------------------------------------------

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Human duration at the granularity the Lowe demo needs — "approval queue:
 * 16 days; actual deliberation: 2 hours" — so hours and days must both read
 * naturally in the same table.
 */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < MINUTE) return `${Math.round(ms / 1000)}s`;
  if (ms < HOUR) return `${Math.round(ms / MINUTE)}m`;
  if (ms < DAY) {
    const hours = ms / HOUR;
    return hours < 10 ? `${hours.toFixed(1)}h` : `${Math.round(hours)}h`;
  }
  const days = ms / DAY;
  return days < 10 ? `${days.toFixed(1)}d` : `${Math.round(days)}d`;
}

/**
 * The narration line. The math computes segments; this states what dominates
 * and proposes nothing — the existing AI-narration posture, except that at this
 * scale the sentence is deterministic and needs no model.
 */
export function narrateAggregate(agg: LatencyAggregate): string {
  if (agg.chains === 0) return 'No resolved chains in range.';

  const offender = agg.p90OffenderStage
    ? agg.stages.find((s) => s.stage === agg.p90OffenderStage)
    : null;

  if (!offender || offender.p90Ms === null) {
    return `${agg.chains} chain${agg.chains === 1 ? '' : 's'} in range; no stage has enough measured segments to name a p90 offender.`;
  }

  const share =
    offender.shareOfMeasuredTime !== null
      ? ` — ${Math.round(offender.shareOfMeasuredTime * 100)}% of measured time`
      : '';

  const gap =
    agg.measurableRatio < 1
      ? ` ${Math.round((1 - agg.measurableRatio) * 100)}% of segments are unmeasured; those stages are absent from this reading, not fast.`
      : '';

  return `${STAGE_LABELS[offender.stage]} dominates: p90 ${formatDuration(offender.p90Ms)}, median ${formatDuration(offender.medianMs)}${share}.${gap}`;
}

// ---------------------------------------------------------------------------
// §5 — Assembly: rows → chains (shared by every fetch layer)
// ---------------------------------------------------------------------------

/** The loop stamps the spine query reads. */
export interface LatencyLoopRow {
  id: string;
  cycle_id: string | null;
  request_issued_at: string | null;
  ball_claimed_at: string | null;
  proposal_submitted_at: string | null;
}

export interface LatencyProposalRow {
  id: string;
  loop_id: string | null;
  session_id: string | null;
  packaged_at: string | null;
  proposed_at: string | null;
  responded_at: string | null;
  resolved_at: string | null;
}

export interface LatencyCycleReportRow {
  id: string;
  cycle_id: string | null;
  created_at: string | null;
  ratified_at: string | null;
}

export interface LatencySessionRow {
  id: string;
  created_at: string | null;
  criteria_finalized_at: string | null;
  run_completed_at: string | null;
}

/**
 * Pairs each loop with its earliest proposal (whose clock the loop's ask
 * started), that proposal's session, and its cycle's latest ratified report
 * (the cycle's authoritative outcome), then builds one chain per loop.
 */
export function assembleLatencyChains(rows: {
  loops: readonly LatencyLoopRow[];
  proposals: readonly LatencyProposalRow[];
  reports: readonly LatencyCycleReportRow[];
  sessions: readonly LatencySessionRow[];
}): LatencyChain[] {
  const proposalByLoop = new Map<string, LatencyProposalRow>();
  for (const p of rows.proposals) {
    if (!p.loop_id) continue;
    const existing = proposalByLoop.get(p.loop_id);
    if (!existing || (p.packaged_at ?? '') < (existing.packaged_at ?? '')) {
      proposalByLoop.set(p.loop_id, p);
    }
  }

  const sessionById = new Map(rows.sessions.map((s) => [s.id, s]));

  const reportByCycle = new Map<string, LatencyCycleReportRow>();
  for (const r of rows.reports) {
    if (!r.cycle_id) continue;
    const existing = reportByCycle.get(r.cycle_id);
    if (!existing || (r.ratified_at ?? '') > (existing.ratified_at ?? '')) {
      reportByCycle.set(r.cycle_id, r);
    }
  }

  return rows.loops.map((loop) => {
    const proposal = proposalByLoop.get(loop.id);
    const session = proposal?.session_id ? sessionById.get(proposal.session_id) : undefined;
    const report = loop.cycle_id ? reportByCycle.get(loop.cycle_id) : undefined;

    return buildLatencyChain({
      id: loop.id,
      label: loop.cycle_id ?? null,
      requestIssuedAt: loop.request_issued_at,
      ballClaimedAt: loop.ball_claimed_at,
      proposalSubmittedAt: loop.proposal_submitted_at,
      sessionCreatedAt: session?.created_at ?? null,
      criteriaFinalizedAt: session?.criteria_finalized_at ?? null,
      runCompletedAt: session?.run_completed_at ?? null,
      packagedAt: proposal?.packaged_at ?? null,
      proposedAt: proposal?.proposed_at ?? null,
      respondedAt: proposal?.responded_at ?? null,
      proposalResolvedAt: proposal?.resolved_at ?? null,
      reportCreatedAt: report?.created_at ?? null,
      reportRatifiedAt: report?.ratified_at ?? null,
      // porAuthorizedAt: deliberately absent — the cycle report → POR join does
      // not exist (migration 678 left source_cycle_report_id NULL on purpose).
    });
  });
}
